import { create } from 'zustand';
import { authApi, oauthApi, OAuthConfig } from '../services/api';
import * as db from '../services/db';

interface User {
  id: number;
  username: string;
  email?: string;
  is_admin: boolean;
  oauth_provider?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  oauthConfig: OAuthConfig | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, email?: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  fetchOAuthConfig: () => Promise<void>;
  handleOAuthCallback: (token: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  oauthConfig: null,

  login: async (username: string, password: string) => {
    const response = await authApi.login({ username, password });
    const { token, user } = response.data;

    localStorage.setItem('token', token);
    set({ user, token, isAuthenticated: true });
  },

  register: async (username: string, password: string, email?: string) => {
    const response = await authApi.register({ username, password, email });
    const { token, user } = response.data;

    localStorage.setItem('token', token);
    set({ user, token, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, token: null, isAuthenticated: false });

    // Clear cached documents since user's access has changed
    db.clearAllDocuments().catch((error) => {
      console.error('[Auth] Failed to clear cache on logout:', error);
    });
  },

  checkAuth: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        set({ user: null, token: null, isAuthenticated: false });
        return;
      }

      // Slide the session forward: refresh issues a new token if the current
      // one is still valid (or recently expired). Keeps mobile users logged
      // in across visits instead of hitting a hard 7-day expiry.
      const response = await authApi.refresh();
      const { token: newToken, user } = response.data;

      localStorage.setItem('token', newToken);
      set({ user, token: newToken, isAuthenticated: true });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false });
    }
  },

  fetchOAuthConfig: async () => {
    try {
      const response = await oauthApi.getConfig();
      set({ oauthConfig: response.data });
    } catch (error) {
      console.error('[Auth] Failed to fetch OAuth config:', error);
      set({ oauthConfig: { enabled: false, providerName: '' } });
    }
  },

  handleOAuthCallback: async (token: string) => {
    localStorage.setItem('token', token);
    
    // Fetch user info with the new token
    try {
      const response = await authApi.getMe();
      set({ user: response.data, token, isAuthenticated: true });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false });
      throw error;
    }
  }
}));

// The API layer clears the token and fires this event when a refresh
// attempt fails, so the UI never stays in a fake logged-in state
window.addEventListener('auth:session-expired', () => {
  const state = useAuthStore.getState();
  if (state.isAuthenticated || state.user) {
    // logout clears the token, resets state and drops the cached docs
    state.logout();
  }
});
